import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CustomEventService } from '@shared/services/custom-event.service';
import ActionTypesEnum from '@shared/enums/action-constant.enum';

import { validateFormGroup } from '@shared/utils/form.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromJS } from 'immutable';

import { OrganizationService } from '@shared/services/organization.service';
import { TeamService } from '@shared/services/team.service';
import { PropertiesService } from "@core/services/properties.service";
import { sortArrayAlphabetically } from "@shared/utils/sort.util";
import { NzNotificationService } from "ng-zorro-antd/notification";

@UntilDestroy()
@Component({
  selector: 'app-add-team-modal',
  templateUrl: './add-team-modal.component.html'
})
export class AddTeamModalComponent implements OnInit,OnDestroy {

  @Input() mode: 'edit' | 'add';
  @Input() data: Team.TeamModel;
  @Input() developers: Developer.DeveloperModel[];
  @Input() readOnly: boolean;
  @Input() loading: boolean;
  teamLeads: Developer.DeveloperModel[];

  levels: OrganizationModel.Organization[];
  upLevelTeams: any[];
  includedTeams: any[];
  teams: any;
  teamName: any;

  addTeamForm: FormGroup;
  formSubmitted = false;
  currentLevel: number;
   inactiveDeveloper: Developer.DeveloperModel[];
  dontUpdatableModal = false;
  upperAndIncludeTeamsSubscription: any;
  constructor(
    private fb: FormBuilder,
    private customEventService: CustomEventService,
    private organizationService: OrganizationService,
    private notificationService: NzNotificationService,
    public propertiesService: PropertiesService,
    private teamService: TeamService) { }

  ngOnInit(): void {
    this.createForm();
    this.init();

    if (this.mode === 'add') {
      this.addTeamForm.get('organizationLevelId').valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(item => {
          const level = this.levels.find(data => data.id === item).level;
          if (item && level) {
            this.getIncludedAndUpperTeams(level, false);

            const roles = this.levels.find(role => role.id === item).leadRoles.map(key => key.key);

            if (this.developers) {
              this.teamLeads = this.developers.filter(developer => roles.find(data => data === developer.companyRole));
            }
          }
        });
    }

    if (this.mode === 'add') {
      this.addTeamForm.get('name').valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(item => {
        if (!this.formSubmitted) {
          this.addTeamForm.get('shortName').setValue(item.substr(0,3).toUpperCase());
        }
      });
    }
  }

  getDeveloperLabelInactive(developer: any): string {

    const label = developer.fullName;
    if (!developer.enabled) {
      return label + ' ' +'(Inactive)';
    }
    return label;
  }

  async getUpperTeams(level, all) {
    this.dontUpdatableModal = true;
    try {
      const response = await this.teamService.getIncludedandUpperTeams(true, level, true, all).toPromise();
      this.upLevelTeams = response;
    } catch (error) {
      console.log('error while fetching values', error);
    }
  }

  async getIncludedAndUpperTeams(level, all): Promise<any> {
    this.upperAndIncludeTeamsSubscription = await this.getUpperTeams(level, all);
    this.teamService.getIncludedandUpperTeams(false, level, true, all)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.includedTeams = response;
        this.dontUpdatableModal = false;
      }, error => {
        this.dontUpdatableModal = false;
        console.log('error while fetching values', error);
      });
  }
  init(): void {
    this.loading = true;
    this.organizationService.get()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.levels = response;
        if (this.mode === 'edit') {
          this.currentLevel = this.levels?.find(item => item.id === this.data?.organizationLevelId)?.level;
          this.getIncludedAndUpperTeams(this.currentLevel, true);
          if (this.data.organizationLevelId) {
            this.addTeamForm.get('organizationLevelId').valueChanges
              .pipe(untilDestroyed(this))
              .subscribe(item => {
                const level = this.levels?.find(res => res.id === item)?.level;
                setTimeout(() => {
                  if (item) {
                    this.getIncludedAndUpperTeams(level, true);
                  }
                });
              });

            const roles = this.levels.find(role => role.id === this.data.organizationLevelId).leadRoles.map(key => key.key);

            if (this.developers) {
              this.teamLeads = this.developers.filter(developer => roles.find(data => data === developer.companyRole));
            }
          }
        }
        this.loading = false;
      }, error => {
        this.loading = false;
        console.log('error while fetching values', error);
      });

    this.developers.sort((a,b) => {
      if (a.id !== b.id) {
        return a.id ? -1 : 1;
      } else {
        return a.id > b.id ? 1: -1
      }
    }).sort((a,b) => {
      if (a.enabled !== b.enabled) {
        return a.enabled ? -1 : 1;
      } else {
        return a.enabled > b.enabled ? 1: -1
      }
    })
  }

  createForm(): void {
    const { id, organizationLevelId, name, shortName, developers = [], leads = [], includedTeams = [], parentIds=[] } = this.data;
    this.addTeamForm = this.fb.group({
      id: [id, []],
      organizationLevelId: [organizationLevelId, [Validators.required]],
      name: [name, [Validators.required]],
      shortName: [shortName, [Validators.required]],
      developers: [developers.sort((a,b) => {
        if (a.enabled !== b.enabled) {
          return a.enabled ? -1 : 1;
        } else {
          return a.enabled > b.enabled ? 1: -1
        }
      }).map(developer => developer.id), []],
      leads: [leads.map(teamLead => teamLead.id), []],
      includedTeams: [includedTeams, []],
      parentIds: [parentIds, []]
    });
  }
  async submitForm(): Promise<void> {
    this.formSubmitted = true;
    validateFormGroup(this.addTeamForm);

    const unitName = this.addTeamForm.value.name;

    if (this.mode === 'add') {
      const isDuplicate = await this.teamNameUniqueness(unitName);

      if (isDuplicate) {
        this.formSubmitted = false;
        this.notificationService.create('error', 'Error', 'The unit name already exists. Please use a different name!');
        return;
      }
    } else if (this.mode === 'edit') {
      const currentTeamName = this.data.name;

      if (unitName.trim().toLowerCase() !== currentTeamName.trim().toLowerCase()) {
        const isDuplicate = await this.teamNameUniqueness(unitName);

        if (isDuplicate) {
          this.formSubmitted = false;
          this.notificationService.create('error', 'Error', 'The unit name already exists. Please use a different name!');
          return;
        }
      }
    }

    if (this.addTeamForm.invalid) {
      this.formSubmitted = false;
      return;
    }

    const formData: any = fromJS(this.addTeamForm.value).filter(val => val);

    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.CREATE_TEAM : ActionTypesEnum.UPDATE_TEAM, formData);
    this.dontUpdatableModal = true;
  }

  teamNameUniqueness(name: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.teamService.list().pipe(untilDestroyed(this)).subscribe(teams => {
        this.teams = teams;

        const normalizedInputName = name.trim().toLowerCase();
        const isTeamExist = this.teams.some(team => team.name.trim().toLowerCase() === normalizedInputName);

        resolve(isTeamExist);
      }, error => {
        console.log('Error while checking if team already exists', error);
        resolve(false);
      });
    });
  }

  resetForm(): void {
    this.createForm();
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.upperAndIncludeTeamsSubscription) {
      this.upperAndIncludeTeamsSubscription.unsubscribe();
    }
  }

  addAll(developers: any): void {
    const values = developers.map(item => item.id);
    this.addTeamForm.get('developers').setValue(values);
  }
}
